import { useRouter } from 'next/router'
import React, { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react'
import { Modal } from 'antd'
import QRCode from 'qrcode.react'

import { useValidateAuth } from '~/hooks/useValidateAuth'
import { useDebounce } from '~/hooks/useDebounce'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { CDNImage } from '~/components/business/CDNImage'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyLink } from '~/components/ui/MyLink'
import { GET_AI_MEMBER_LIST } from '~/graphqls/gqls'
import {
  AccountGroupTypeEnum,
  getAIMemberList,
  getAIMemberListVariables,
  MemberCardRightTypeEnum,
} from '~/graphqls/types'

import classNames from 'classnames'

const { confirm } = Modal

const HOST = process.env.NEXT_PUBLIC_WUJIEBANTU_HOST

interface IProps {
  className?: string
  dark?: boolean
}
export const Footer: React.FC<IProps> = observer(({ className, dark }) => {
  const { validateIsLogin } = useValidateAuth()
  const router = useRouter()

  const QRCodes = useMemo(() => {
    return [
      {
        url: 'https://work.weixin.qq.com/u/vc18ff79c145b296c9?v=4.1.3.21882&src=wx',
        label: 'AI创作交流微信群',
      },
      {
        url: 'https://work.weixin.qq.com/u/vc4186b138aa4211aa?v=4.0.16.19901',
        label: '会员管家',
      },
      {
        url: process.env.NEXT_PUBLIC_MOBILE_HOST + '/download',
        label: '扫码下载APP',
      },
    ]
  }, [])

  const apiMemberList = useImperativeQuery<
    getAIMemberList,
    getAIMemberListVariables
  >(GET_AI_MEMBER_LIST)

  // 用户拥有的ai权益卡
  const getMemberList = useCallback(() => {
    return apiMemberList({
      groupList: [
        AccountGroupTypeEnum.BLANK_PASS,
        AccountGroupTypeEnum.BRONZE_CARD,
        AccountGroupTypeEnum.GOLD_CARD,
        AccountGroupTypeEnum.ORIGIN_CARD,
        AccountGroupTypeEnum.PLATINUM_CARD,
        AccountGroupTypeEnum.SILVER_CARD,
      ],
    }).then(res => res.data.accountModule?.userMemberCardList || [])
  }, [apiMemberList])

  // 存证
  const onCertificate = useDebounce(async () => {
    if (validateIsLogin()) {
      let evidenceCount = 0
      try {
        const aiMemberList = await getMemberList()
        // 免费存证次数
        aiMemberList?.forEach(card => {
          card.holdCardInfo?.forEach(i => {
            if (i?.rightType === MemberCardRightTypeEnum.EVIDENCE_CREATE) {
              evidenceCount += i.num || 0
            }
          })
        })
      } catch (error) {
        console.error('获取ai权益卡失败', error)
      }

      if (evidenceCount > 0) {
        // 跳转到无界版图：上传存证
        window.open(HOST + '/upload-certificate')
      } else {
        confirm({
          closable: true,
          title: '提示',
          content: '购买AI创作权益卡可赠送免费存证次数',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          className: 'hookModal dark',
          width: 300,
          onOk: () => {
            router.push('/ai/equity-card')
          },
          okText: '开通权益卡',
          cancelText: '取消',
        })
      }
    }
  }, [validateIsLogin, getMemberList])

  return (
    <footer
      className={classNames(
        ' border-t-[1px] border-neutral-300',
        dark
          ? ' border-neutral-300 bg-neutral-100'
          : ' border-neutral-600 bg-neutral-900',
        className
      )}
    >
      <MyContainer>
        <div className="px-12">
          <div
            className={classNames(
              'py-6 flex justify-between border-b-[1px] ',
              dark ? 'border-b-neutral-300' : 'border-b-neutral-600'
            )}
          >
            <div>
              <CDNImage
                className="mb-4 h-10"
                src={dark ? '/images/logo-1.svg' : '/images/logo--black.svg'}
                alt=""
              />
              <h1
                className={classNames(
                  ' mb-4 text-base !leading-6',
                  dark ? 'text-neutral-600' : 'text-neutral-300'
                )}
              >
                人人都是艺术家
              </h1>
              <div className="flex text-neutral-400">
                <span
                  onClick={onCertificate}
                  className="cursor-pointer hover:text-[#553ca0]"
                >
                  版权存证
                </span>
                <a
                  href="https://data.wujiebantu.com/Wujie_Logopack_v3.0.zip"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="ml-6"
                >
                  品牌素材
                </a>
                <MyLink href="/us/privacy" exist={true} className="ml-6">
                  隐私政策
                </MyLink>
                <MyLink href="/us/user-agreement" exist={true} className="ml-6">
                  用户协议
                </MyLink>
              </div>
            </div>
            <div className="flex justify-end text-center">
              {QRCodes.map(item => (
                <div className="ml-8" key={item.url}>
                  <div className="border-[3px] border-neutral-800 mb-2">
                    <QRCode
                      value={item.url}
                      size={90}
                      fgColor="#000000"
                      bgColor="#ffffff"
                    ></QRCode>
                  </div>
                  <div className="text-xs text-neutral-400">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-neutral-400 py-6 flex justify-between">
            <span>
              Copyright © 2022-{new Date().getFullYear()} 无界AI 版权所有
              <a
                href="https://beian.cac.gov.cn"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="ml-2"
              >
                网信算备330110556840601240015号
              </a>
              <a
                href="https://beian.miit.gov.cn"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="ml-2"
              >
                浙ICP备2021000434号
              </a>
            </span>
          </div>
        </div>
      </MyContainer>
    </footer>
  )
})
